import Image from 'next/image'
import { useState, SyntheticEvent, CSSProperties, useEffect } from 'react'

import generics from '@/utils/generics'
import { useStoreActions, useStoreState } from '@/store'
import { Agent, Location } from '@/types/agents'
import { getAgentTransactions } from '@/utils/agent-data'

import Icon from '../elements/Icon'
import Tooltip from '../elements/Tooltip'
import AgentProfile from '../profile/AgentProfile'

type Props = {
  agent: Agent
  index: number
  location: Location
}

const avatarStyle: CSSProperties = {
  borderRadius: '50%',
  objectFit: 'cover',
  objectPosition: 'top center'
}

const AgentRow = ({ agent, index, location }: Props) => {
  const [show, setShow] = useState(false)
  const [url, setUrl] = useState(agent.details.destination_url)

  const {
    onElementClickedTracking,
    onProductClickedTracking,
    onProductViewedTracking
  } = useStoreActions(actions => actions.tracking)

  const { listId } = useStoreState(actions => actions.search)

  useEffect(() => {
    const productViewedPayload = generics.buildProductViewedPayload(
      listId,
      agent.full_name || '',
      agent.details.specialties ? agent.details.specialties.join(',') : '',
      agent.id,
      'agent-table',
      index + 1
    )
    onProductViewedTracking(productViewedPayload)

    // if page is not within iframe, add ref_url to links
    if (window === window.top) {
      const url = new URL(agent.details.destination_url)
      url.searchParams.set('ref_url', window.location.href)
      setUrl(url.toString())
    }
  }, [])

  const agentProfileClick = () => {
    setShow(true)

    const elementClickedPayload = generics.buildElementClickedPayload(
      'button',
      agent.full_name || '',
      (index + 1).toString(),
      'view profile',
      'View profile',
      'MODAL'
    )

    onElementClickedTracking(elementClickedPayload)
  }

  const agentProfileClose = () => {
    setShow(false)

    document.getElementsByTagName('html')[0].style.overflow = ''

    const elementClickedPayload = generics.buildElementClickedPayload(
      'button',
      agent.full_name || '',
      'profile top',
      'close modal',
      'Close button',
      'close modal'
    )

    onElementClickedTracking(elementClickedPayload)
  }

  const connectClick = (event: SyntheticEvent) => {
    const productClickedPayload = generics.buildProductClickedPayload(
      listId,
      agent.full_name || '',
      agent.details.specialties ? agent.details.specialties.join(',') : '',
      agent.id,
      'agent-table',
      index + 1
    )
    onProductClickedTracking(productClickedPayload)
  }

  const { totalTransactions, localTransactions } = getAgentTransactions(agent)

  return (
    <>
      <div
        className="shadow-sm p-4 lg:p-6 rounded-lg grid grid-cols-1 lg:grid-cols-12 mb-6"
        data-testid="agent-row"
      >
        <div className="hidden lg:grid lg:col-span-2">
          <Image
            src={agent.details.avatar_url}
            alt={`${agent.first_name} ${agent.last_name}`}
            width={112}
            height={112}
            style={avatarStyle}
            loading="lazy"
          />
        </div>
        <div className="lg:col-span-8 lg:mr-2 lg:ml-4">
          <div className="grid grid-cols-3 gap-2 lg:grid-cols-1 items-center mb-4 lg:mb-0">
            <div className="col-span-1 lg:hidden">
              <Image
                src={agent.details.avatar_url}
                alt={`${agent.first_name} ${agent.last_name}`}
                width={96}
                height={96}
                style={avatarStyle}
                loading="lazy"
              />
            </div>
            <div className="col-span-2 lg:col-span-1">
              <p className="mb-0 text-gray-900 font-bold">
                {agent.first_name} {agent.last_name}
              </p>
              <div className="mb-4 overflow-hidden">
                <div className="lg:flex flex-wrap justify-start inline-block lg:-ml-5 ml-0 gap-y-2">
                  <p className="lg:inline-block text-sm text-gray-700 mb-0 lg:ml-5">
                    {agent.agency?.name || ''}
                  </p>
                  {agent.details.experience_years && (
                    <p className="divider lg:inline-block text-sm text-gray-700 mb-0">
                      {agent.details.experience_years} years in business
                    </p>
                  )}

                  <p className="divider lg:inline-block text-sm text-gray-700 mb-0">
                    {totalTransactions} closings in the last 12 mos
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 xl:grid-cols-6 gap-4 lg:gap-0 mb-6 lg:mb-0">
            <div className="xl:col-span-2">
              <p className="text-xs text-gray-700 mb-1">
                Closings in {location.city}, {location.state}
              </p>
              <span className="text-gray-900 font-bold type-heading-four">
                {localTransactions}
              </span>
            </div>
            <div className="xl:col-span-2">
              <p className="text-xs text-gray-700 mb-1">
                Avg time on market
              </p>
              <Tooltip
                testId="avg-time-tip"
                tooltipName="Average time on market"
                buttonText={
                  <span className="text-gray-900 font-bold type-heading-four">
                    {agent.details.avg_days_on_market || '--'} days
                  </span>
                }
                className="pb-1 mb-0 text-gray-700"
                arrowPosition="anchorCenter"
                underline={true}
                agentId={agent.id || ''}
                index={index}
                text="Avg time on market"
              >
                <p className="text-sm">
                  <strong className="font-bold">
                    Average time on market:
                  </strong>{' '}
                  Average number of days this agent’s home sale listings stay on
                  the market.
                </p>
              </Tooltip>
            </div>
            <div className="xl:col-span-2">
              <p className="text-xs text-gray-700 mb-1">
                Sold-to-list price ratio
              </p>
              <Tooltip
                testId="price-ratio-tip"
                tooltipName="Sold-to-list price ratio"
                buttonText={
                  <span className="text-gray-900 font-bold type-heading-four">
                    {Math.round(agent.details.avg_sell_to_list_ratio * 100) ||
                      '--'}
                    %
                  </span>
                }
                className="pb-1 mb-0 text-gray-700"
                arrowPosition="anchorRight"
                underline={true}
                agentId={agent.id || ''}
                index={index}
                text="Sold-to-list price ratio"
              >
                <p className="text-sm">
                  <strong className="font-bold">
                    Sold-to-list price ratio:
                  </strong>{' '}
                  The final sale price (what a buyer pays for the home) divided
                  by the last list price expressed as a percentage.
                </p>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 grid grid-cols-2 lg:block">
          <button
            data-testid="view-profile"
            onClick={agentProfileClick}
            className="Button Button--secondary lg:block lg:mb-4 mr-3 lg:mr-0 px-5 lg:w-full lg:px-0"
          >
            View profile
          </button>
          <a
            data-testid="connect"
            onClick={connectClick}
            href={url}
            target="_blank"
            className="Button Button--primary lg:w-full lg:px-0"
            rel="nofollow, noreferrer"
          >
            Connect
            <Icon variant="arrow" className="fill-white Icon--xs ml-1 mb-2" />
          </a>
        </div>
      </div>

      <AgentProfile
        testId="close-profile"
        onClose={agentProfileClose}
        show={show}
        agent={agent}
        location={location}
      />
    </>
  )
}

export default AgentRow
